import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminUser, AdminService } from '../services/adminService';
import { Timestamp } from 'firebase/firestore';
import { isAdminEmail } from '../config/adminConfig';

interface UserDetailModalProps {
  user: AdminUser | null;
  isOpen: boolean;
  onClose: () => void;
  onUserUpdate?: (updatedUser: AdminUser) => void;
  onSuccess?: (message: string) => void;
  onError?: (message: string) => void;
}

const UserDetailModal: React.FC<UserDetailModalProps> = ({ user, isOpen, onClose, onUserUpdate, onSuccess, onError }) => {
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [editedUser, setEditedUser] = useState<AdminUser | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setEditedUser({ ...user });
    }
  }, [user]);

  if (!isOpen || !user || !editedUser) return null;

  const formatTimestamp = (timestamp: Timestamp) => {
    return timestamp.toDate().toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getRoleBadgeColor = (role: string, email: string) => {
    if (isAdminEmail(email)) {
      return 'bg-red-100 text-red-800';
    }
    switch (role) {
      case 'admin':
        return 'bg-purple-100 text-purple-800';
      case 'editor':
        return 'bg-blue-100 text-blue-800';
      case 'viewer':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'suspended':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleInputChange = (field: keyof AdminUser, value: string | boolean) => {
    setEditedUser(prev => prev ? { ...prev, [field]: value } : null);
  };

  const handleSave = async () => {
    if (!editedUser || !user) return;
    
    setIsSaving(true);
    try {
      // Prepare updates object with only the changed fields
      const updates: Partial<AdminUser> = {};
      
      // Check which fields have changed and add them to updates
      if (editedUser.displayName !== user.displayName) {
        updates.displayName = editedUser.displayName;
      }
      if (editedUser.firstName !== user.firstName) {
        updates.firstName = editedUser.firstName;
      }
      if (editedUser.lastName !== user.lastName) {
        updates.lastName = editedUser.lastName;
      }
      if (editedUser.companyName !== user.companyName) {
        updates.companyName = editedUser.companyName;
      }
      if (editedUser.role !== user.role) {
        updates.role = editedUser.role;
      }
      if (editedUser.status !== user.status) {
        updates.status = editedUser.status;
      }
      if (editedUser.onboardingCompleted !== user.onboardingCompleted) {
        updates.onboardingCompleted = editedUser.onboardingCompleted;
      }
      if (editedUser.twoFactorEnabled !== user.twoFactorEnabled) {
        updates.twoFactorEnabled = editedUser.twoFactorEnabled;
      }

      // Only update if there are changes
      if (Object.keys(updates).length > 0) {
        await AdminService.updateUser(user.id, updates);
        
        // Call the update callback to refresh the UI
        if (onUserUpdate) {
          onUserUpdate(editedUser);
        }
        
        // Show success message
        if (onSuccess) {
          onSuccess('User details updated successfully!');
        }
      } else {
        // No changes made
        if (onSuccess) {
          onSuccess('No changes to save.');
        }
      }
      
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving user:', error);
      console.error('Error details:', {
        userId: user.id,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
        errorStack: error instanceof Error ? error.stack : undefined
      });
      
      const errorMessage = error instanceof Error ? error.message : 'Failed to save user changes. Please try again.';
      
      if (onError) {
        onError(errorMessage);
      } else {
        alert(errorMessage);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditedUser({ ...user });
    setIsEditing(false);
  };

  const handleViewUserDetail = () => {
    if (user) {
      navigate(`/admin/user-contacts/${user.id}`);
      onClose(); // Close the modal
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="h-12 w-12 rounded-full bg-gray-300 flex items-center justify-center">
              <span className="text-lg font-medium text-gray-700">
                {editedUser.email.charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {isEditing ? (
                  <input
                    type="text"
                    value={editedUser.displayName || editedUser.firstName || ''}
                    onChange={(e) => handleInputChange('displayName', e.target.value)}
                    className="text-xl font-semibold bg-transparent border-b border-gray-300 focus:border-blue-500 focus:outline-none"
                    placeholder="Display Name"
                  />
                ) : (
                  editedUser.displayName || editedUser.firstName || editedUser.email
                )}
              </h2>
              <p className="text-sm text-gray-500">{editedUser.email}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {isEditing ? (
              <>
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={handleCancel}
                  disabled={isSaving}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  Cancel
                </button>
              </>
            ) : (
              <div className="flex space-x-3">
                <button
                  onClick={handleViewUserDetail}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
                >
                  View User Detail
                </button>
                <button
                  onClick={() => setIsEditing(true)}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Edit User
                </button>
              </div>
            )}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Basic Information</h3>
              
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-gray-500">Full Name</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedUser.displayName || ''}
                      onChange={(e) => handleInputChange('displayName', e.target.value)}
                      className="mt-1 block w-full text-sm text-gray-900 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Display Name"
                    />
                  ) : (
                    <p className="text-sm text-gray-900">
                      {editedUser.displayName || editedUser.firstName || 'Not provided'}
                    </p>
                  )}
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-500">Email Address</label>
                  <p className="text-sm text-gray-900">{editedUser.email}</p>
                  <p className="text-xs text-gray-400">Email cannot be changed</p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-500">First Name</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedUser.firstName || ''}
                      onChange={(e) => handleInputChange('firstName', e.target.value)}
                      className="mt-1 block w-full text-sm text-gray-900 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="First Name"
                    />
                  ) : (
                    <p className="text-sm text-gray-900">{editedUser.firstName || 'Not provided'}</p>
                  )}
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-500">Last Name</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedUser.lastName || ''}
                      onChange={(e) => handleInputChange('lastName', e.target.value)}
                      className="mt-1 block w-full text-sm text-gray-900 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Last Name"
                    />
                  ) : (
                    <p className="text-sm text-gray-900">{editedUser.lastName || 'Not provided'}</p>
                  )}
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-500">Company</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedUser.companyName || ''}
                      onChange={(e) => handleInputChange('companyName', e.target.value)}
                      className="mt-1 block w-full text-sm text-gray-900 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Company Name"
                    />
                  ) : (
                    <p className="text-sm text-gray-900">{editedUser.companyName || 'Not provided'}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Account Status */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Account Status</h3>
              
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-gray-500">Role</label>
                  <div className="mt-1">
                    {isEditing ? (
                      <select
                        value={editedUser.role}
                        onChange={(e) => handleInputChange('role', e.target.value as 'admin' | 'editor' | 'viewer')}
                        className="block w-full text-sm text-gray-900 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        disabled={isAdminEmail(editedUser.email)}
                      >
                        <option value="viewer">Viewer</option>
                        <option value="editor">Editor</option>
                        <option value="admin">Admin</option>
                      </select>
                    ) : (
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleBadgeColor(editedUser.role, editedUser.email)}`}>
                        {isAdminEmail(editedUser.email) ? 'Admin (Email)' : editedUser.role.charAt(0).toUpperCase() + editedUser.role.slice(1)}
                      </span>
                    )}
                    {isAdminEmail(editedUser.email) && (
                      <p className="text-xs text-gray-400 mt-1">Email-based admin role cannot be changed</p>
                    )}
                  </div>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-500">Status</label>
                  <div className="mt-1">
                    {isEditing ? (
                      <select
                        value={editedUser.status}
                        onChange={(e) => handleInputChange('status', e.target.value as 'active' | 'pending' | 'suspended')}
                        className="block w-full text-sm text-gray-900 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="active">Active</option>
                        <option value="pending">Pending</option>
                        <option value="suspended">Suspended</option>
                      </select>
                    ) : (
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeColor(editedUser.status)}`}>
                        {editedUser.status.charAt(0).toUpperCase() + editedUser.status.slice(1)}
                      </span>
                    )}
                  </div>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-500">Onboarding</label>
                  <div className="mt-1">
                    {isEditing ? (
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={editedUser.onboardingCompleted || false}
                          onChange={(e) => handleInputChange('onboardingCompleted', e.target.checked)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-900">Onboarding Completed</span>
                      </label>
                    ) : (
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        editedUser.onboardingCompleted 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {editedUser.onboardingCompleted ? 'Completed' : 'Pending'}
                      </span>
                    )}
                  </div>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-500">Two-Factor Authentication</label>
                  <div className="mt-1">
                    {isEditing ? (
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={editedUser.twoFactorEnabled || false}
                          onChange={(e) => handleInputChange('twoFactorEnabled', e.target.checked)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-900">2FA Enabled</span>
                      </label>
                    ) : (
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        editedUser.twoFactorEnabled 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {editedUser.twoFactorEnabled ? 'Enabled' : 'Disabled'}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Timestamps */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Account Timeline</h3>
              
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-gray-500">Account Created</label>
                  <p className="text-sm text-gray-900">{formatTimestamp(user.createdAt)}</p>
                </div>
                
                {user.lastActive && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Last Active</label>
                    <p className="text-sm text-gray-900">{formatTimestamp(user.lastActive)}</p>
                  </div>
                )}
                
                {user.lastLoginAt && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Last Login</label>
                    <p className="text-sm text-gray-900">{formatTimestamp(user.lastLoginAt)}</p>
                  </div>
                )}
                
                {user.invitedAt && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Invited At</label>
                    <p className="text-sm text-gray-900">{formatTimestamp(user.invitedAt)}</p>
                  </div>
                )}
                
                {user.invitedBy && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Invited By</label>
                    <p className="text-sm text-gray-900">{user.invitedBy}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Additional Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Additional Information</h3>
              
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-gray-500">User ID</label>
                  <p className="text-sm text-gray-900 font-mono">{user.id}</p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-500">Email Verified</label>
                  <p className="text-sm text-gray-900">
                    {user.email ? 'Yes' : 'No'}
                  </p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-500">Account Type</label>
                  <p className="text-sm text-gray-900">
                    {isAdminEmail(user.email) ? 'Administrator' : 'Regular User'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          {!isEditing && (
            <div className="mt-8 pt-6 border-t border-gray-200">
              <div className="flex justify-end space-x-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserDetailModal;
